import Vue from "vue"
import {PostContentType} from "@chaosinsight/postoffice-portalclient"

import {watch} from "@/store/store"

import Configuration from "@/managers/system/configuration"

import Portal from "@/managers/portal"

import Organizations, {Organization} from "@/managers/organization/organizations"

import Publisher from "@/managers/data/organization/publisher"
import Notifications from "@/managers/session/notifications"

class Publishers {
	private readonly state: {map: Record<number, Publisher> | null}

	public get isReady(): boolean {
		return this.state.map !== null
	}

	public get list(): Publisher[] {
		return this.state.map !== null
			? Object.values(this.state.map).sort((a, b) => a.id - b.id)
			: []
	}

	public get noneBuiltIn(): Publisher[] {
		return this.list.filter(publisher => !publisher.isBuiltIn)
	}

	public get hasOnlyBuiltIn(): boolean {
		if (!this.isReady)
			return true

		for (const publisher of this.list)
			if (!publisher.isBuiltIn)
				return false

		return true
	}

	public get facebookId(): number {
		return Configuration.publishers.builtIn.Facebook
	}

	public get instagramId(): number {
		return Configuration.publishers.builtIn.Instagram
	}

	public get facebook(): Publisher {
		return this.getById(this.facebookId)
	}

	public get instagram(): Publisher {
		return this.getById(this.instagramId)
	}

	private get mainOrganization(): Organization | null {
		return Organizations.main
	}

	constructor() {
		this.state = Vue.observable({ map: null })

		watch(
			() => {
				return this.mainOrganization !== null
					? this.mainOrganization.id
					: null
			},
			organizationId => {
				this.state.map = null

				if (organizationId !== null)
					this.updateList(organizationId)
						.catch(reason => Notifications.error("Failed to update publishers", reason))
			},
			true
		)
	}

	public async create(name: string, isEventsEndpoint: boolean, endpointUrl: string): Promise<Publisher> {
		if (!this.isReady)
			throw new Error("Publishers are not ready")

		const organizationId = Organizations.main!.id

		const data = await Portal.call.organizations.publishersPost(organizationId, name, endpointUrl, isEventsEndpoint, [PostContentType.regular]).response

		const publisher = Publisher.fromApi(data, this.mainOrganization!.logoUrl!)

		Vue.set(this.state.map!, publisher.id, publisher)

		return publisher
	}

	public async modify(publisherId: number, name: string, isEventsEndpoint: boolean, endpointUrl: string, secretKey?: string): Promise<Publisher> {
		if (!this.isReady)
			throw new Error("Publishers are not ready")

		const data = await Portal.call.publishers.put(publisherId, name, endpointUrl, isEventsEndpoint, [PostContentType.regular], secretKey).response
		const existing = this.state.map![publisherId]

		if (existing === undefined)
			throw new Error("Publisher not found")

		return existing!.updateFromApi(data)
	}

	public getById(id: number): Publisher {
		if (!this.isReady)
			throw new Error("Publishers are not ready")

		const publisher = this.state.map![id]

		if (publisher === undefined)
			throw new Error(`Publisher with id: ${id} not found`)

		return publisher
	}

	public getNameOrEmpty(id: number): string {
		if (!this.isReady)
			return ""
		return this.getById(id).name
	}

	public getLogoUrl(id: number): string {
		if (!this.isReady)
			return Configuration.general.fallbackLogo
		return this.getById(id).logoUrl
	}

	private async updateList(organizationId: number): Promise<void> {
		const data = await Portal.call.organizations.publishersGet(organizationId).response

		if (organizationId !== Organizations.main?.id)
			return

		this.state.map = data.reduce((map, publisher) => {
			map[publisher.Id] = Publisher.fromApi(publisher, this.mainOrganization!.logoUrl!)
			return map
		}, {} as Record<number, Publisher>)
	}
}

export {Publisher}
export default new Publishers()
